/**
 * FeldModus - Handschuh-freundliche Schnellzugriff-Oberfläche
 * 
 * Optimiert für:
 * - Große Touch-Targets (80x80px+)
 * - Bedienung mit Arbeitshandschuhen
 * - Outdoor-Sichtbarkeit (hoher Kontrast)
 * - Ein-Hand-Bedienung
 * - Schnellzugriff auf wichtigste Funktionen
 * - OFFLINE-SUPPORT: Uploads werden lokal gespeichert und automatisch synchronisiert
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Camera, Mic, MicOff, CheckCircle2, AlertTriangle, 
  ArrowLeft, FolderOpen, Clock, Send, X, Volume2,
  Image, FileText, ChevronRight, Loader2, Wifi, WifiOff, Cloud
} from 'lucide-react';
import { getApiUrl } from '../utils/api';
import { savePendingUpload, initOfflineDB, countPendingUploads } from '../utils/offlineStorage';
import { setupAutoSync, addSyncListener, syncPendingUploads } from '../utils/offlineSync';
import OfflineIndicator from './OfflineIndicator';

const FeldModus = ({ currentUser, onBack }) => {
  const { t } = useTranslation();
  
  // State
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [openTasks, setOpenTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeMode, setActiveMode] = useState(null); // 'photo', 'voice', 'task', 'defect', 'defect-describe', 'task-complete-photo', 'task-complete-voice'
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [defectDescription, setDefectDescription] = useState('');
  const [defectAudioBlob, setDefectAudioBlob] = useState(null);
  const [defectAudioTranscript, setDefectAudioTranscript] = useState('');
  const [isDefectRecording, setIsDefectRecording] = useState(false);
  const [defectRecordingTime, setDefectRecordingTime] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(''); // For upload status display
  const [successMessage, setSuccessMessage] = useState('');
  
  // Task Completion Wizard State
  const [completingTask, setCompletingTask] = useState(null); // The task being completed
  const [taskCompletionPhoto, setTaskCompletionPhoto] = useState(null);
  const [taskCompletionAudioBlob, setTaskCompletionAudioBlob] = useState(null);
  const [taskCompletionTranscript, setTaskCompletionTranscript] = useState('');
  const [isTaskRecording, setIsTaskRecording] = useState(false);
  const [taskRecordingTime, setTaskRecordingTime] = useState(0);
  const taskRecorderRef = useRef(null);
  const taskChunksRef = useRef([]);
  const taskTimerRef = useRef(null);
  
  // Offline State
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingUploads, setPendingUploads] = useState(0);
  
  // Refs
  const mediaRecorderRef = useRef(null);
  const defectRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const defectChunksRef = useRef([]);
  const timerRef = useRef(null);
  const defectTimerRef = useRef(null);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  // Initialize Offline DB and Auto-Sync
  useEffect(() => {
    initOfflineDB();
    const cleanupAutoSync = setupAutoSync();
    
    // Online/Offline Status
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Sync-Events abonnieren
    const unsubscribe = addSyncListener((event, data) => {
      if (event === 'sync-complete') {
        updatePendingCount();
      }
    });
    
    // Initial count
    updatePendingCount();
    
    return () => {
      cleanupAutoSync();
      unsubscribe();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const updatePendingCount = async () => {
    const count = await countPendingUploads();
    setPendingUploads(count);
  };

  // Load projects on mount
  useEffect(() => {
    loadProjects();
    return () => {
      // Cleanup camera stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Load user's projects
  const loadProjects = async () => {
    if (!currentUser?.id) return;
    
    try {
      const response = await fetch(`${getApiUrl()}/projects/user/${currentUser.id}`);
      const data = await response.json();
      const projectList = Array.isArray(data) ? data : (data.projects || []);
      setProjects(projectList);
      
      // Auto-select last used project or first project
      const lastProjectId = localStorage.getItem('lastActiveProject');
      const lastProject = projectList.find(p => p.id === lastProjectId);
      if (lastProject) {
        setSelectedProject(lastProject);
        loadOpenTasks(lastProject.id);
      } else if (projectList.length > 0) {
        setSelectedProject(projectList[0]);
        loadOpenTasks(projectList[0].id);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load open tasks for project
  const loadOpenTasks = async (projectId) => {
    try {
      const response = await fetch(`${getApiUrl()}/tasks/project/${projectId}`);
      const data = await response.json();
      const tasks = (data.tasks || []).filter(t => t.status === 'open');
      setOpenTasks(tasks.slice(0, 5)); // Show max 5 tasks
    } catch (error) {
      console.error('Error loading tasks:', error);
    }
  };

  // Select project
  const selectProject = (project) => {
    setSelectedProject(project);
    localStorage.setItem('lastActiveProject', project.id);
    loadOpenTasks(project.id);
    setActiveMode(null);
  };

  // Start camera - set mode first, then stream will be attached via useEffect
  const startCamera = async () => {
    setActiveMode('photo');
  };
  
  // Effect to start camera stream when photo or defect mode is activated
  useEffect(() => {
    const needsCamera = (activeMode === 'photo' || activeMode === 'defect') && !capturedPhoto;
    
    if (needsCamera) {
      const initCamera = async () => {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ 
            video: { facingMode: 'environment' }, 
            audio: false 
          });
          streamRef.current = stream;
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            await videoRef.current.play();
          }
        } catch (error) {
          console.error('Camera error:', error);
          alert('Kamera-Zugriff nicht möglich: ' + error.message);
          setActiveMode(null);
        }
      };
      initCamera();
    }
    
    // Cleanup when leaving photo/defect mode
    return () => {
      if (activeMode !== 'photo' && activeMode !== 'defect' && streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, [activeMode, capturedPhoto]);

  // Capture photo
  const capturePhoto = () => {
    if (!videoRef.current) return;
    
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth || 640;
    canvas.height = videoRef.current.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
    
    canvas.toBlob((blob) => {
      if (blob) {
        setCapturedPhoto(blob);
        // Stop camera
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
      }
    }, 'image/jpeg', 0.85);
  };

  // Start voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Determine supported MIME type
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') 
        ? 'audio/webm' 
        : MediaRecorder.isTypeSupported('audio/mp4') 
          ? 'audio/mp4' 
          : 'audio/ogg';
      
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        console.log('Recording complete, blob size:', blob.size);
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      // Request data every second for more reliable recording
      mediaRecorderRef.current.start(1000);
      setIsRecording(true);
      setActiveMode('voice');
      
      // Start timer
      setRecordingTime(0);
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Microphone error:', error);
      alert('Mikrofon-Zugriff nicht möglich: ' + error.message);
    }
  };

  // Stop voice recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);
    }
  };

  // Submit photo - with offline support
  const submitPhoto = async () => {
    if (!capturedPhoto || !selectedProject) return;
    
    setSubmitting(true);
    
    // Check if offline - save locally
    if (!navigator.onLine) {
      setUploadProgress('Offline - wird lokal gespeichert...');
      try {
        await savePendingUpload({
          type: 'photo',
          blob: capturedPhoto,
          projectId: selectedProject.id,
          userId: currentUser.id,
          userName: currentUser.name,
          caption: '📸 Foto vom Einsatzort'
        });
        await updatePendingCount();
        showSuccess('📴 Foto lokal gespeichert!\nWird automatisch synchronisiert.');
        resetState();
      } catch (error) {
        console.error('Offline save error:', error);
        alert('Fehler beim lokalen Speichern: ' + error.message);
      } finally {
        setSubmitting(false);
        setUploadProgress('');
      }
      return;
    }
    
    setUploadProgress('Foto wird hochgeladen...');
    try {
      // Use the same endpoint as Chat component
      const formData = new FormData();
      formData.append('file', capturedPhoto, `baustelle_foto_${Date.now()}.jpg`);
      
      const response = await fetch(
        `${getApiUrl()}/upload/chat?project_id=${selectedProject.id}&sender_id=${currentUser.id}&caption=${encodeURIComponent('📸 Foto vom Einsatzort')}`,
        {
          method: 'POST',
          body: formData
        }
      );
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Photo upload error:', errorText);
        throw new Error('Upload failed: ' + errorText);
      }
      
      const result = await response.json();
      console.log('Photo sent:', result);
      
      showSuccess('Foto erfolgreich gesendet!');
      resetState();
    } catch (error) {
      console.error('Submit error:', error);
      // On network error, save offline
      if (!navigator.onLine || error.message.includes('fetch')) {
        setUploadProgress('Netzwerkfehler - wird lokal gespeichert...');
        try {
          await savePendingUpload({
            type: 'photo',
            blob: capturedPhoto,
            projectId: selectedProject.id,
            userId: currentUser.id,
            userName: currentUser.name,
            caption: '📸 Foto vom Einsatzort'
          });
          await updatePendingCount();
          showSuccess('📴 Foto lokal gespeichert!\nWird automatisch synchronisiert.');
          resetState();
        } catch (saveError) {
          alert('Fehler beim Senden: ' + error.message);
        }
      } else {
        alert('Fehler beim Senden: ' + error.message);
      }
    } finally {
      setSubmitting(false);
      setUploadProgress('');
    }
  };

  // Submit voice note - with offline support
  const submitVoice = async () => {
    if (!audioBlob || !selectedProject) return;
    
    setSubmitting(true);
    
    // Check if offline - save locally
    if (!navigator.onLine) {
      setUploadProgress('Offline - wird lokal gespeichert...');
      try {
        await savePendingUpload({
          type: 'voice',
          blob: audioBlob,
          projectId: selectedProject.id,
          userId: currentUser.id,
          userName: currentUser.name,
          caption: '🎤 Sprachnotiz vom Einsatzort',
          metadata: { language: currentUser.language || 'de' }
        });
        await updatePendingCount();
        showSuccess('📴 Sprachnotiz lokal gespeichert!\nWird automatisch synchronisiert.');
        resetState();
      } catch (error) {
        console.error('Offline save error:', error);
        alert('Fehler beim lokalen Speichern: ' + error.message);
      } finally {
        setSubmitting(false);
        setUploadProgress('');
      }
      return;
    }
    
    setUploadProgress('Sprachnotiz wird hochgeladen...');
    try {
      // Use the same endpoint as Chat component
      const formData = new FormData();
      formData.append('file', audioBlob, `baustelle_voice_${Date.now()}.webm`);
      
      const response = await fetch(
        `${getApiUrl()}/upload/chat?project_id=${selectedProject.id}&sender_id=${currentUser.id}&caption=${encodeURIComponent('🎤 Sprachnotiz vom Einsatzort')}`,
        {
          method: 'POST',
          body: formData
        }
      );
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Voice upload error:', errorText);
        throw new Error('Voice upload failed: ' + errorText);
      }
      
      const result = await response.json();
      console.log('Voice message sent:', result);
      
      // Optionally trigger transcription
      setUploadProgress('Transkription läuft...');
      if (result.file_path) {
        try {
          await fetch(`${getApiUrl()}/transcribe-voice`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              project_id: selectedProject.id,
              sender_id: currentUser.id,
              file_path: result.file_path,
              language: currentUser.language || 'de'
            })
          });
          console.log('Transcription requested');
        } catch (transcribeError) {
          console.log('Transcription skipped:', transcribeError);
        }
      }
      
      showSuccess('Sprachnotiz gesendet!');
      resetState();
    } catch (error) {
      console.error('Voice submit error:', error);
      // On network error, save offline
      if (!navigator.onLine || error.message.includes('fetch')) {
        setUploadProgress('Netzwerkfehler - wird lokal gespeichert...');
        try {
          await savePendingUpload({
            type: 'voice',
            blob: audioBlob,
            projectId: selectedProject.id,
            userId: currentUser.id,
            userName: currentUser.name,
            caption: '🎤 Sprachnotiz vom Einsatzort',
            metadata: { language: currentUser.language || 'de' }
          });
          await updatePendingCount();
          showSuccess('📴 Sprachnotiz lokal gespeichert!\nWird automatisch synchronisiert.');
          resetState();
        } catch (saveError) {
          alert('Fehler beim Senden: ' + error.message);
        }
      } else {
        alert('Fehler beim Senden: ' + error.message);
      }
    } finally {
      setSubmitting(false);
      setUploadProgress('');
    }
  };

  // Mark task as complete
  const completeTask = async (task) => {
    setSubmitting(true);
    setUploadProgress('Task wird aktualisiert...');
    try {
      const response = await fetch(`${getApiUrl()}/tasks/${task.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'completed',
          completed_at: new Date().toISOString(),
          completed_by: currentUser.id
        })
      });
      
      if (!response.ok) throw new Error('Update failed');
      
      showSuccess(`"${task.title}" erledigt!`);
      loadOpenTasks(selectedProject.id);
    } catch (error) {
      console.error('Task complete error:', error);
      alert('Fehler beim Aktualisieren');
    } finally {
      setSubmitting(false);
      setUploadProgress('');
    }
  };

  // ===== TASK COMPLETION WIZARD FUNCTIONS =====
  
  // Start task completion - go to photo step
  const startTaskCompletion = (task) => {
    setCompletingTask(task);
    setTaskCompletionPhoto(null);
    setTaskCompletionAudioBlob(null);
    setTaskCompletionTranscript('');
    setActiveMode('task-complete-photo');
  };

  // Reset task completion state
  const resetTaskCompletion = () => {
    setCompletingTask(null);
    setTaskCompletionPhoto(null);
    setTaskCompletionAudioBlob(null);
    setTaskCompletionTranscript('');
    setIsTaskRecording(false);
    setTaskRecordingTime(0);
    if (taskTimerRef.current) clearInterval(taskTimerRef.current);
    setActiveMode(null);
  };

  // Handle task completion photo capture
  const onTaskPhotoCaptured = () => {
    setActiveMode('task-complete-voice');
  };

  // Start recording for task completion
  const startTaskRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') 
        ? 'audio/webm' 
        : MediaRecorder.isTypeSupported('audio/mp4') 
          ? 'audio/mp4' 
          : 'audio/ogg';
      
      taskRecorderRef.current = new MediaRecorder(stream, { mimeType });
      taskChunksRef.current = [];
      
      taskRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          taskChunksRef.current.push(e.data);
        }
      };
      
      taskRecorderRef.current.onstop = async () => {
        const blob = new Blob(taskChunksRef.current, { type: mimeType });
        setTaskCompletionAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
        
        // Auto-transcribe
        setUploadProgress('Transkription läuft...');
        try {
          const formData = new FormData();
          formData.append('file', blob, 'task_completion.webm');
          formData.append('language', currentUser.language || 'de');
          
          const response = await fetch(`${getApiUrl()}/transcribe`, {
            method: 'POST',
            body: formData
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.text) {
              setTaskCompletionTranscript(data.text);
            }
          }
        } catch (e) {
          console.log('Transcription failed:', e);
        } finally {
          setUploadProgress('');
        }
      };
      
      taskRecorderRef.current.start();
      setIsTaskRecording(true);
      setTaskRecordingTime(0);
      
      taskTimerRef.current = setInterval(() => {
        setTaskRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Recording error:', error);
      alert('Mikrofon nicht verfügbar');
    }
  };

  // Stop task recording
  const stopTaskRecording = () => {
    if (taskRecorderRef.current && taskRecorderRef.current.state === 'recording') {
      taskRecorderRef.current.stop();
    }
    setIsTaskRecording(false);
    if (taskTimerRef.current) {
      clearInterval(taskTimerRef.current);
    }
  };

  // Submit task completion with photo and optional voice note
  const submitTaskCompletion = async () => {
    if (!completingTask) return;
    
    setSubmitting(true);
    setUploadProgress('Task wird abgeschlossen...');
    
    try {
      // Upload photo if captured
      let photoUrl = null;
      if (taskCompletionPhoto) {
        setUploadProgress('Foto wird hochgeladen...');
        const formData = new FormData();
        formData.append('file', taskCompletionPhoto);
        formData.append('uploader_id', currentUser.id);
        
        const uploadRes = await fetch(`${getApiUrl()}/files/upload/${completingTask.project_id}`, {
          method: 'POST',
          body: formData
        });
        
        if (uploadRes.ok) {
          const uploadData = await uploadRes.json();
          photoUrl = uploadData.file_path;
        }
      }
      
      // Complete task with proof
      setUploadProgress('Task wird aktualisiert...');
      const completeUrl = new URL(`${getApiUrl()}/tasks/${completingTask.id}/complete`);
      completeUrl.searchParams.append('user_id', currentUser.id);
      completeUrl.searchParams.append('description', taskCompletionTranscript || '');
      
      await fetch(completeUrl.toString(), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          photos: photoUrl ? [photoUrl] : [],
          description: taskCompletionTranscript || 'Erledigt'
        })
      });
      
      showSuccess(`"${completingTask.title}" erledigt!`);
      loadOpenTasks(selectedProject.id);
      resetTaskCompletion();
      
    } catch (error) {
      console.error('Task completion error:', error);
      alert('Fehler beim Abschließen: ' + error.message);
    } finally {
      setSubmitting(false);
      setUploadProgress('');
    }
  };

  // Start defect photo capture
  const startDefectPhoto = () => {
    setActiveMode('defect');
  };

  // After defect photo captured, move to description step
  const onDefectPhotoCaptured = () => {
    // Photo is already in capturedPhoto state
    setActiveMode('defect-describe');
  };

  // Start recording defect description
  const startDefectRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') 
        ? 'audio/webm' 
        : MediaRecorder.isTypeSupported('audio/mp4') 
          ? 'audio/mp4' 
          : 'audio/ogg';
      
      defectRecorderRef.current = new MediaRecorder(stream, { mimeType });
      defectChunksRef.current = [];
      
      defectRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          defectChunksRef.current.push(e.data);
        }
      };
      
      defectRecorderRef.current.onstop = async () => {
        const blob = new Blob(defectChunksRef.current, { type: mimeType });
        setDefectAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
        
        // Auto-transcribe the audio (best-effort, don't block on failure)
        setUploadProgress('Transkription läuft...');
        try {
          const formData = new FormData();
          formData.append('file', blob, 'defect_description.webm');
          formData.append('language', currentUser.language || 'de');
          
          console.log('🎤 Starte Transkription...');
          const response = await fetch(`${getApiUrl()}/transcribe-audio`, {
            method: 'POST',
            body: formData
          });
          
          if (response.ok) {
            const result = await response.json();
            console.log('✅ Transkription Ergebnis:', result);
            // Backend returns "text", not "transcript"
            const transcribedText = result.text || result.transcript;
            if (transcribedText) {
              setDefectDescription(transcribedText);
              setDefectAudioTranscript(transcribedText);
              console.log('✅ Transkription in Textbox übernommen:', transcribedText);
            } else {
              console.log('⚠️ Keine Transkription erhalten, Antwort:', result);
            }
          } else {
            const errorText = await response.text();
            console.error('❌ Transkription fehlgeschlagen:', response.status, errorText);
            // Don't show alert - user can still submit with audio
          }
        } catch (err) {
          console.error('❌ Transkription Fehler:', err);
          // Don't show alert - user can still submit with audio
        } finally {
          setUploadProgress('');
        }
      };
      
      defectRecorderRef.current.start(1000);
      setIsDefectRecording(true);
      
      setDefectRecordingTime(0);
      defectTimerRef.current = setInterval(() => {
        setDefectRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Microphone error:', error);
      alert('Mikrofon-Zugriff nicht möglich: ' + error.message);
    }
  };

  // Stop defect recording
  const stopDefectRecording = () => {
    if (defectRecorderRef.current && isDefectRecording) {
      defectRecorderRef.current.stop();
      setIsDefectRecording(false);
      clearInterval(defectTimerRef.current);
    }
  };

  // Submit defect report with photo and description - with offline support
  const submitDefect = async () => {
    if (!selectedProject) return;
    
    // Validate: need at least a photo OR description OR audio
    const hasPhoto = !!capturedPhoto;
    const hasDescription = !!defectDescription?.trim();
    const hasAudio = !!defectAudioBlob;
    
    if (!hasPhoto && !hasDescription && !hasAudio) {
      alert('Bitte fügen Sie eine Beschreibung oder ein Foto hinzu');
      return;
    }
    
    setSubmitting(true);
    
    // Use description or fallback text
    const finalDescription = hasDescription 
      ? defectDescription.trim()
      : hasAudio 
        ? '(Sprachbeschreibung - Transkription ausstehend)'
        : 'Mangel - siehe Foto';
    
    // Check if offline - save locally
    if (!navigator.onLine) {
      setUploadProgress('Offline - wird lokal gespeichert...');
      try {
        await savePendingUpload({
          type: 'defect',
          blob: capturedPhoto,
          projectId: selectedProject.id,
          userId: currentUser.id,
          userName: currentUser.name,
          description: finalDescription,
          metadata: { hasPhoto, hasAudio }
        });
        await updatePendingCount();
        showSuccess('📴 Mangel lokal gespeichert!\nWird automatisch synchronisiert.');
        resetDefectState();
        resetState();
      } catch (error) {
        console.error('Offline save error:', error);
        alert('Fehler beim lokalen Speichern: ' + error.message);
      } finally {
        setSubmitting(false);
        setUploadProgress('');
      }
      return;
    }
    
    setUploadProgress('Mangel wird gemeldet...');
    
    try {
      // 1. Create task FIRST
      setUploadProgress('Task wird erstellt...');
      const title = finalDescription.length > 50 
        ? finalDescription.substring(0, 50) + '...' 
        : finalDescription;
      
      const taskData = {
        project_id: selectedProject.id,
        title: `⚠️ ${title}`,
        description: finalDescription,
        priority: 'urgent',
        tags: ['Mangel', 'Einsatzort']
      };
      
      console.log('📝 Erstelle Mangel-Task:', taskData);
      
      const response = await fetch(`${getApiUrl()}/tasks?created_by=${currentUser.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(taskData)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Task-Erstellung fehlgeschlagen:', errorText);
        throw new Error('Task konnte nicht erstellt werden: ' + errorText);
      }
      
      const createdTask = await response.json();
      console.log('✅ Task erstellt:', createdTask);
      const taskId = createdTask.id;
      
      // 2. Upload photo as task attachment
      if (hasPhoto && taskId) {
        setUploadProgress('Foto wird angehängt...');
        const photoFormData = new FormData();
        photoFormData.append('file', capturedPhoto, `mangel_foto_${Date.now()}.jpg`);
        photoFormData.append('uploader_id', currentUser.id);
        photoFormData.append('attachment_type', 'task');
        
        try {
          const photoResponse = await fetch(
            `${getApiUrl()}/tasks/${taskId}/attachments`,
            { method: 'POST', body: photoFormData }
          );
          
          if (photoResponse.ok) {
            console.log('✅ Foto an Task angehängt');
          } else {
            console.error('❌ Foto-Anhang fehlgeschlagen:', await photoResponse.text());
          }
        } catch (photoError) {
          console.error('Foto-Upload fehlgeschlagen:', photoError);
        }
      }
      
      // 3. Upload audio as task attachment (if exists and no transcription)
      if (hasAudio && !hasDescription && taskId) {
        setUploadProgress('Audio wird angehängt...');
        const audioFormData = new FormData();
        audioFormData.append('file', defectAudioBlob, `mangel_audio_${Date.now()}.webm`);
        audioFormData.append('uploader_id', currentUser.id);
        audioFormData.append('attachment_type', 'task');
        
        try {
          const audioResponse = await fetch(
            `${getApiUrl()}/tasks/${taskId}/attachments`,
            { method: 'POST', body: audioFormData }
          );
          
          if (audioResponse.ok) {
            console.log('✅ Audio an Task angehängt');
          } else {
            console.error('❌ Audio-Anhang fehlgeschlagen:', await audioResponse.text());
          }
        } catch (audioError) {
          console.error('Audio-Upload fehlgeschlagen:', audioError);
        }
      }
      
      // Mangel wird NUR als Task gespeichert, KEINE Chat-Nachricht
      
      showSuccess('✅ Mangel als Task gemeldet!');
      resetDefectState();
      resetState();
    } catch (error) {
      console.error('Defect submit error:', error);
      // On network error, save offline
      if (!navigator.onLine || error.message.includes('fetch') || error.message.includes('network')) {
        setUploadProgress('Netzwerkfehler - wird lokal gespeichert...');
        try {
          await savePendingUpload({
            type: 'defect',
            blob: capturedPhoto,
            projectId: selectedProject.id,
            userId: currentUser.id,
            userName: currentUser.name,
            description: finalDescription,
            metadata: { hasPhoto, hasAudio }
          });
          await updatePendingCount();
          showSuccess('📴 Mangel lokal gespeichert!\nWird automatisch synchronisiert.');
          resetDefectState();
          resetState();
        } catch (saveError) {
          alert('Fehler beim Melden: ' + error.message);
        }
      } else {
        alert('Fehler beim Melden: ' + error.message);
      }
    } finally {
      setSubmitting(false);
      setUploadProgress('');
    }
  };

  // Reset defect-specific state
  const resetDefectState = () => {
    setDefectDescription('');
    setDefectAudioBlob(null);
    setDefectAudioTranscript('');
    setIsDefectRecording(false);
    setDefectRecordingTime(0);
    if (defectTimerRef.current) {
      clearInterval(defectTimerRef.current);
    }
  };

  // Show success message
  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // Reset state
  const resetState = () => {
    setActiveMode(null);
    setCapturedPhoto(null);
    setAudioBlob(null);
    setDefectDescription('');
    setRecordingTime(0);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
  };

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-2xl">{t('common.loading')}</div>
      </div>
    );
  }

  // No project selected - show project selector
  if (!selectedProject) {
    return (
      <div className="h-screen max-h-screen bg-gray-900 p-4 flex flex-col">
        <div className="flex items-center justify-between mb-6 flex-shrink-0">
          <button 
            onClick={onBack}
            className="p-4 bg-gray-800 rounded-2xl text-white"
          >
            <ArrowLeft size={32} />
          </button>
          <h1 className="text-2xl font-bold text-white">{t('feldModus.selectProject')}</h1>
          <div className="w-16"></div>
        </div>
        
        <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pb-24">
          {projects.length === 0 ? (
            <p className="text-gray-400 text-center py-8">{t('feldModus.noProjects')}</p>
          ) : (
            projects.map(project => (
              <button
                key={project.id}
                onClick={() => selectProject(project)}
                className="w-full p-6 bg-gray-800 rounded-2xl text-left text-white 
                           flex items-center justify-between
                           active:bg-gray-700 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <FolderOpen size={40} className="text-emerald-400" />
                  <div>
                    <div className="text-xl font-bold">{project.name}</div>
                    <div className="text-gray-400 text-sm">
                      {project.description 
                        ? project.description 
                        : project.created_at 
                          ? t('projects.createdOn', 'Erstellt am') + ' ' + new Date(project.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
                          : (project.members?.length > 0 
                              ? `${project.members.length} ${t('projects.members', 'Mitglieder')}` 
                              : t('projects.noDescription', 'Projekt'))}
                    </div>
                  </div>
                </div>
                <ChevronRight size={32} className="text-gray-500" />
              </button>
            ))
          )}
        </div>
      </div>
    );
  }

  // Success overlay
  if (successMessage) {
    return (
      <div className="min-h-screen bg-emerald-600 flex items-center justify-center p-8">
        <div className="text-center">
          <CheckCircle2 size={120} className="text-white mx-auto mb-6" />
          <div className="text-3xl font-bold text-white">{successMessage}</div>
        </div>
      </div>
    );
  }

  // Photo capture mode
  if (activeMode === 'photo' && !capturedPhoto) {
    return (
      <div className="min-h-screen bg-black flex flex-col">
        <video 
          ref={videoRef} 
          autoPlay 
          playsInline 
          className="flex-1 object-cover"
        />
        <div className="absolute bottom-0 left-0 right-0 p-6 flex justify-center gap-6">
          <button
            onClick={resetState}
            className="p-6 bg-gray-800 rounded-full text-white"
          >
            <X size={40} />
          </button>
          <button
            onClick={capturePhoto}
            className="p-8 bg-white rounded-full"
          >
            <Camera size={48} className="text-gray-900" />
          </button>
        </div>
      </div>
    );
  }

  // Photo preview mode
  if (capturedPhoto && activeMode === 'photo') {
    return (
      <div className="min-h-screen bg-gray-900 flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <img 
            src={URL.createObjectURL(capturedPhoto)} 
            alt="Captured" 
            className="max-h-full max-w-full rounded-2xl"
          />
        </div>
        
        {/* Upload progress indicator */}
        {uploadProgress && (
          <div className="mx-4 mb-4 p-4 bg-blue-900/50 rounded-2xl border-2 border-blue-500">
            <div className="flex items-center gap-4">
              <Loader2 size={28} className="text-blue-400 animate-spin" />
              <span className="text-blue-300 text-lg">{uploadProgress}</span>
            </div>
          </div>
        )}
        
        <div className="p-6 flex justify-center gap-6">
          <button
            onClick={resetState}
            disabled={submitting}
            className="p-6 bg-red-600 rounded-2xl text-white flex items-center gap-3
                       disabled:opacity-50"
          >
            <X size={32} />
            <span className="text-xl font-bold">{t('feldModus.discard')}</span>
          </button>
          <button
            onClick={submitPhoto}
            disabled={submitting}
            className="p-6 bg-emerald-600 rounded-2xl text-white flex items-center gap-3
                       disabled:opacity-50"
          >
            {submitting ? (
              <>
                <Loader2 size={32} className="animate-spin" />
                <span className="text-xl font-bold">{t('feldModus.sending')}</span>
              </>
            ) : (
              <>
                <Send size={32} />
                <span className="text-xl font-bold">{t('feldModus.send')}</span>
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  // Voice recording mode
  if (activeMode === 'voice') {
    return (
      <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center p-8">
        <div className="text-center mb-12">
          <div className={`w-40 h-40 rounded-full flex items-center justify-center mx-auto mb-6 
                          ${isRecording ? 'bg-red-600 animate-pulse' : 'bg-gray-700'}`}>
            {isRecording ? (
              <Mic size={80} className="text-white" />
            ) : (
              <Volume2 size={80} className="text-emerald-400" />
            )}
          </div>
          <div className="text-5xl font-mono text-white mb-4">
            {formatTime(recordingTime)}
          </div>
          <div className="text-xl text-gray-400">
            {isRecording ? t('feldModus.recording') : t('feldModus.audioRecorded')}
          </div>
        </div>
        
        {/* Upload progress indicator */}
        {uploadProgress && (
          <div className="w-full max-w-sm mb-6 p-4 bg-blue-900/50 rounded-2xl border-2 border-blue-500">
            <div className="flex items-center justify-center gap-4">
              <Loader2 size={28} className="text-blue-400 animate-spin" />
              <span className="text-blue-300 text-lg">{uploadProgress}</span>
            </div>
          </div>
        )}
        
        <div className="flex gap-6">
          <button
            onClick={resetState}
            disabled={submitting}
            className="p-6 bg-gray-700 rounded-2xl text-white disabled:opacity-50"
          >
            <X size={40} />
          </button>
          
          {isRecording ? (
            <button
              onClick={stopRecording}
              className="p-8 bg-red-600 rounded-2xl text-white"
            >
              <MicOff size={48} />
            </button>
          ) : audioBlob ? (
            <button
              onClick={submitVoice}
              disabled={submitting}
              className="p-6 bg-emerald-600 rounded-2xl text-white flex items-center gap-3
                         disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <Loader2 size={32} className="animate-spin" />
                  <span className="text-xl font-bold">{t('feldModus.sending')}</span>
                </>
              ) : (
                <>
                  <Send size={32} />
                  <span className="text-xl font-bold">{t('feldModus.send')}</span>
                </>
              )}
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  // Task list mode
  if (activeMode === 'task') {
    return (
      <div className="min-h-screen bg-gray-900 p-4">
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={() => setActiveMode(null)}
            className="p-4 bg-gray-800 rounded-2xl text-white"
          >
            <ArrowLeft size={32} />
          </button>
          <h1 className="text-2xl font-bold text-white">{t('tasks.openTasks', 'Open Tasks')}</h1>
          <div className="w-16"></div>
        </div>
        
        {openTasks.length === 0 ? (
          <div className="text-center text-gray-400 text-xl py-12">
            {t('tasks.noOpenTasks', 'No open tasks')}
          </div>
        ) : (
          <div className="space-y-4">
            {openTasks.map(task => (
              <div
                key={task.id}
                className="p-6 bg-gray-800 rounded-2xl"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="text-xl font-bold text-white mb-2">{task.title}</div>
                    {task.description && (
                      <div className="text-gray-400 line-clamp-2">{task.description}</div>
                    )}
                    {task.priority === 'urgent' && (
                      <span className="inline-block mt-2 px-3 py-1 bg-red-600 text-white text-sm rounded-full">
                        ⚠️ {t('tasks.urgent', 'Urgent')}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => startTaskCompletion(task)}
                    disabled={submitting}
                    className="p-6 bg-emerald-600 rounded-2xl text-white shrink-0 flex flex-col items-center"
                  >
                    <CheckCircle2 size={32} />
                    <span className="text-xs mt-1">{t('feldModus.complete', 'Erledigt')}</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ===== TASK COMPLETION WIZARD - STEP 1: PHOTO =====
  if (activeMode === 'task-complete-photo') {
    return (
      <div className="min-h-screen bg-black flex flex-col">
        {/* Camera view */}
        <video 
          ref={videoRef} 
          autoPlay 
          playsInline 
          className="flex-1 object-cover"
        />
        
        {/* Header overlay */}
        <div className="absolute top-0 left-0 right-0 p-4 bg-gradient-to-b from-black/70 to-transparent">
          <div className="flex items-center justify-between">
            <button 
              onClick={resetTaskCompletion}
              className="p-3 bg-white/20 backdrop-blur rounded-full"
            >
              <ArrowLeft className="text-white" size={24} />
            </button>
            <div className="text-center">
              <p className="text-white font-bold text-lg">{t('feldModus.step1', 'Schritt 1')}</p>
              <p className="text-emerald-400 text-sm">{t('feldModus.takeProofPhoto', 'Nachweis-Foto')}</p>
            </div>
            <div className="w-12" />
          </div>
          
          {/* Task info */}
          <div className="mt-4 p-3 bg-white/10 backdrop-blur rounded-xl">
            <p className="text-white text-sm truncate">{completingTask?.title}</p>
          </div>
        </div>
        
        {/* Bottom controls */}
        <div className="absolute bottom-0 left-0 right-0 p-6 pb-12 bg-gradient-to-t from-black/80 to-transparent">
          <div className="flex items-center justify-center gap-6">
            {/* Skip button */}
            <button
              onClick={onTaskPhotoCaptured}
              className="p-4 bg-white/20 backdrop-blur rounded-full text-white text-sm"
            >
              {t('feldModus.skip', 'Überspringen')}
            </button>
            
            {/* Capture button */}
            <button
              onClick={async () => {
                if (videoRef.current) {
                  const canvas = document.createElement('canvas');
                  canvas.width = videoRef.current.videoWidth;
                  canvas.height = videoRef.current.videoHeight;
                  canvas.getContext('2d').drawImage(videoRef.current, 0, 0);
                  canvas.toBlob((blob) => {
                    setTaskCompletionPhoto(blob);
                    onTaskPhotoCaptured();
                  }, 'image/jpeg', 0.85);
                }
              }}
              className="w-20 h-20 bg-emerald-500 rounded-full border-4 border-white flex items-center justify-center shadow-lg"
            >
              <Camera className="text-white" size={36} />
            </button>
            
            {/* Spacer */}
            <div className="w-16" />
          </div>
        </div>
      </div>
    );
  }

  // ===== TASK COMPLETION WIZARD - STEP 2: VOICE/CONFIRM =====
  if (activeMode === 'task-complete-voice') {
    return (
      <div className="min-h-screen bg-gray-900 flex flex-col">
        {/* Header */}
        <div className="p-4 bg-gray-800 shrink-0">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => setActiveMode('task-complete-photo')}
              className="p-3 bg-gray-700 rounded-xl text-white"
            >
              <ArrowLeft size={28} />
            </button>
            <div className="text-center">
              <p className="text-white font-bold text-lg">{t('feldModus.step2', 'Schritt 2')}</p>
              <p className="text-emerald-400">{t('feldModus.addNote', 'Notiz hinzufügen')}</p>
            </div>
            <div className="w-12" />
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Task info */}
          <div className="p-4 bg-gray-800 rounded-2xl">
            <p className="text-gray-400 text-sm">{t('feldModus.task', 'Aufgabe')}</p>
            <p className="text-white text-lg font-medium">{completingTask?.title}</p>
          </div>
          
          {/* Photo preview */}
          {taskCompletionPhoto && (
            <div className="relative">
              <img 
                src={URL.createObjectURL(taskCompletionPhoto)} 
                alt="Nachweis" 
                className="w-full h-48 object-cover rounded-2xl"
              />
              <div className="absolute top-2 right-2 px-3 py-1 bg-emerald-600 rounded-full text-white text-sm">
                ✓ {t('feldModus.photoTaken', 'Foto aufgenommen')}
              </div>
            </div>
          )}
          
          {/* Voice recording */}
          <div className="space-y-3">
            <p className="text-gray-400">{t('feldModus.optionalVoiceNote', 'Optionale Sprachnotiz')}</p>
            
            {!isTaskRecording ? (
              <button
                onClick={startTaskRecording}
                disabled={uploadProgress !== ''}
                className="w-full p-6 bg-gray-800 rounded-2xl text-white flex items-center justify-center gap-4 border-2 border-gray-700 hover:border-emerald-500"
              >
                <Mic size={32} className="text-emerald-400" />
                <span className="text-xl">{uploadProgress || t('feldModus.tapToRecord', 'Antippen zum Aufnehmen')}</span>
              </button>
            ) : (
              <button
                onClick={stopTaskRecording}
                className="w-full p-6 bg-red-600 rounded-2xl text-white flex items-center justify-center gap-4 animate-pulse"
              >
                <MicOff size={32} />
                <span className="text-xl">{taskRecordingTime}s - {t('feldModus.tapToStop', 'Antippen zum Stoppen')}</span>
              </button>
            )}
            
            {/* Transcript preview */}
            {taskCompletionTranscript && (
              <div className="p-4 bg-emerald-900/30 rounded-2xl border border-emerald-600">
                <p className="text-emerald-400 text-sm mb-1">{t('feldModus.transcript', 'Transkript')}</p>
                <p className="text-white">{taskCompletionTranscript}</p>
              </div>
            )}
            
            {taskCompletionAudioBlob && !taskCompletionTranscript && (
              <div className="p-4 bg-gray-800 rounded-2xl">
                <p className="text-emerald-400">✓ {t('feldModus.voiceRecorded', 'Sprachnotiz aufgenommen')}</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Submit button */}
        <div className="p-4 pb-24 bg-gray-800 shrink-0">
          <button
            onClick={submitTaskCompletion}
            disabled={submitting}
            className="w-full p-6 bg-emerald-600 rounded-2xl text-white text-2xl font-bold
                       disabled:opacity-50 flex items-center justify-center gap-4"
          >
            {submitting ? (
              <>
                <Loader2 size={32} className="animate-spin" />
                <span>{uploadProgress || t('feldModus.submitting', 'Wird gesendet...')}</span>
              </>
            ) : (
              <>
                <CheckCircle2 size={32} />
                <span>{t('feldModus.completeTask', 'Task abschließen')}</span>
              </>
            )}
          </button>
          
          {/* Quick complete without voice */}
          {!taskCompletionAudioBlob && !taskCompletionTranscript && (
            <button
              onClick={submitTaskCompletion}
              disabled={submitting}
              className="w-full mt-3 p-4 bg-gray-700 rounded-xl text-gray-300 text-lg"
            >
              {t('feldModus.completeWithoutNote', 'Ohne Notiz abschließen')}
            </button>
          )}
        </div>
      </div>
    );
  }

  // Defect report mode - Step 1: Photo capture
  if (activeMode === 'defect') {
    return (
      <div className="min-h-screen bg-black flex flex-col">
        {/* Camera view */}
        <video 
          ref={videoRef} 
          autoPlay 
          playsInline 
          className="flex-1 object-cover"
        />
        
        {/* Header overlay */}
        <div className="absolute top-0 left-0 right-0 p-4 bg-gradient-to-b from-black/70 to-transparent">
          <div className="flex items-center justify-between">
            <button 
              onClick={resetState}
              className="p-4 bg-gray-800/80 rounded-2xl text-white"
            >
              <ArrowLeft size={32} />
            </button>
            <div className="text-center">
              <h1 className="text-xl font-bold text-white">{t('feldModus.step1of2')}</h1>
              <p className="text-amber-400 text-lg">{t('feldModus.defectPhoto')}</p>
            </div>
            <div className="w-16"></div>
          </div>
        </div>
        
        {/* Capture button */}
        <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/70 to-transparent">
          <div className="flex justify-center gap-6">
            <button
              onClick={resetState}
              className="p-6 bg-gray-800/80 rounded-full text-white"
            >
              <X size={40} />
            </button>
            <button
              onClick={() => {
                capturePhoto();
                // After capture, move to describe step
                setTimeout(() => setActiveMode('defect-describe'), 300);
              }}
              className="p-8 bg-amber-500 rounded-full shadow-lg shadow-amber-500/50"
            >
              <Camera size={48} className="text-white" />
            </button>
          </div>
          <p className="text-center text-gray-300 mt-4 text-lg">
            {t('feldModus.tapToCapture')}
          </p>
        </div>
      </div>
    );
  }

  // Defect report mode - Step 2: Add description
  if (activeMode === 'defect-describe') {
    return (
      <div className="min-h-screen bg-gray-900 flex flex-col">
        {/* Header */}
        <div className="p-4 bg-gray-800 shrink-0">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => {
                resetDefectState();
                setCapturedPhoto(null);
                setActiveMode('defect');
              }}
              className="p-3 bg-gray-700 rounded-xl text-white"
            >
              <ArrowLeft size={28} />
            </button>
            <div className="text-center">
              <h1 className="text-xl font-bold text-white">{t('feldModus.step2of2')}</h1>
              <p className="text-amber-400">{t('feldModus.addDescription')}</p>
            </div>
            <div className="w-12"></div>
          </div>
        </div>
        
        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto pb-4">
          {/* Photo preview */}
          {capturedPhoto && (
            <div className="p-4">
              <div className="relative">
                <img 
                  src={URL.createObjectURL(capturedPhoto)} 
                  alt="Mangel-Foto" 
                  className="w-full h-48 object-cover rounded-2xl"
                />
                <div className="absolute top-2 right-2 px-3 py-1 bg-emerald-600 rounded-full text-white text-sm">
                  ✓ {t('feldModus.photoTaken')}
                </div>
              </div>
            </div>
          )}
          
          {/* Description options */}
          <div className="p-4 space-y-4">
            {/* Text input */}
            <div>
              <label className="text-gray-400 text-lg mb-2 block">{t('feldModus.textDescription')}</label>
              <textarea
                value={defectDescription}
                onChange={(e) => setDefectDescription(e.target.value)}
                placeholder={t('feldModus.descriptionPlaceholder')}
                className="w-full p-4 bg-gray-800 rounded-2xl text-white text-lg 
                           placeholder-gray-500 min-h-[120px] resize-none border-2 border-gray-700
                           focus:border-amber-500 focus:outline-none"
            />
          </div>
          
          {/* Voice recording option */}
          <div className="text-gray-400 text-lg mb-2">{t('feldModus.orVoiceDescription')}</div>
          
          {isDefectRecording ? (
            <div className="p-6 bg-red-900/50 rounded-2xl border-2 border-red-500">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-4 h-4 bg-red-500 rounded-full animate-pulse"></div>
                  <span className="text-white text-xl font-mono">{formatTime(defectRecordingTime)}</span>
                  <span className="text-red-400">{t('feldModus.recording')}</span>
                </div>
                <button
                  onClick={stopDefectRecording}
                  className="p-4 bg-red-600 rounded-xl text-white"
                >
                  <MicOff size={32} />
                </button>
              </div>
            </div>
          ) : defectAudioBlob ? (
            <div className="p-6 bg-emerald-900/50 rounded-2xl border-2 border-emerald-500">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Volume2 size={32} className="text-emerald-400" />
                  <div>
                    <span className="text-white text-lg">{t('feldModus.audioRecorded')}</span>
                    {defectAudioTranscript && (
                      <p className="text-gray-400 text-sm mt-1 line-clamp-2">
                        "{defectAudioTranscript}"
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setDefectAudioBlob(null);
                    setDefectAudioTranscript('');
                  }}
                  className="p-3 bg-gray-700 rounded-xl text-white"
                >
                  <X size={24} />
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={startDefectRecording}
              className="w-full p-6 bg-gray-800 rounded-2xl text-white flex items-center justify-center gap-4
                         border-2 border-gray-700 active:border-purple-500 transition-colors"
            >
              <Mic size={36} className="text-purple-400" />
              <span className="text-xl">{t('feldModus.tapToRecord')}</span>
            </button>
          )}
          
          {/* Upload progress indicator */}
          {uploadProgress && (
            <div className="p-4 bg-blue-900/50 rounded-2xl border-2 border-blue-500">
              <div className="flex items-center gap-4">
                <Loader2 size={28} className="text-blue-400 animate-spin" />
                <span className="text-blue-300 text-lg">{uploadProgress}</span>
              </div>
            </div>
          )}
          </div>
        </div>
        
        {/* Submit button - with iOS safe area padding */}
        <div className="p-4 pb-24 bg-gray-800 shrink-0">
          <button
            onClick={submitDefect}
            disabled={submitting || (!defectDescription && !defectAudioBlob)}
            className="w-full p-6 bg-amber-600 rounded-2xl text-white text-2xl font-bold
                       disabled:opacity-50 disabled:cursor-not-allowed
                       flex items-center justify-center gap-4"
          >
            {submitting ? (
              <>
                <Loader2 size={32} className="animate-spin" />
                <span>{t('feldModus.reporting')}</span>
              </>
            ) : (
              <>
                <AlertTriangle size={32} />
                <span>{t('feldModus.reportDefectBtn')}</span>
              </>
            )}
          </button>
          
          {/* Skip description option */}
          {!defectDescription && !defectAudioBlob && capturedPhoto && (
            <button
              onClick={() => {
                setDefectDescription('Mangel - siehe Foto');
                setTimeout(() => submitDefect(), 100);
              }}
              disabled={submitting}
              className="w-full mt-3 p-4 bg-gray-700 rounded-xl text-gray-300 text-lg
                         disabled:opacity-50"
            >
              {t('feldModus.sendPhotoOnly')}
            </button>
          )}
        </div>
      </div>
    );
  }

  // Main quick action screen
  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      {/* Header */}
      <div className="p-4 bg-gray-800">
        <div className="flex items-center justify-between">
          <button 
            onClick={onBack}
            className="p-3 bg-gray-700 rounded-xl text-white"
          >
            <ArrowLeft size={28} />
          </button>
          
          <button
            onClick={() => setSelectedProject(null)}
            className="flex items-center gap-3 px-4 py-2 bg-gray-700 rounded-xl text-white"
          >
            <FolderOpen size={24} className="text-emerald-400" />
            <span className="font-bold truncate max-w-[200px]">{selectedProject.name}</span>
          </button>
          
          <div className="p-3 bg-emerald-600 rounded-xl text-white">
            <Clock size={28} />
          </div>
        </div>
      </div>

      {/* Main action buttons */}
      <div className="flex-1 p-4 grid grid-cols-2 gap-4 content-start">
        {/* Photo */}
        <button
          onClick={startCamera}
          className="aspect-square bg-blue-600 rounded-3xl flex flex-col items-center justify-center gap-4
                     active:bg-blue-700 transition-colors shadow-lg"
        >
          <Camera size={64} className="text-white" />
          <span className="text-xl font-bold text-white">{t('feldModus.photo')}</span>
        </button>

        {/* Voice */}
        <button
          onClick={startRecording}
          className="aspect-square bg-purple-600 rounded-3xl flex flex-col items-center justify-center gap-4
                     active:bg-purple-700 transition-colors shadow-lg"
        >
          <Mic size={64} className="text-white" />
          <span className="text-xl font-bold text-white text-center px-2">{t('feldModus.voiceNote')}</span>
        </button>

        {/* Task complete */}
        <button
          onClick={() => setActiveMode('task')}
          className="aspect-square bg-emerald-600 rounded-3xl flex flex-col items-center justify-center gap-4
                     active:bg-emerald-700 transition-colors shadow-lg relative"
        >
          <CheckCircle2 size={64} className="text-white" />
          <span className="text-xl font-bold text-white text-center px-2">{t('feldModus.taskDone')}</span>
          {openTasks.length > 0 && (
            <span className="absolute top-4 right-4 w-10 h-10 bg-red-500 rounded-full 
                           flex items-center justify-center text-white text-xl font-bold">
              {openTasks.length}
            </span>
          )}
        </button>

        {/* Defect report */}
        <button
          onClick={() => setActiveMode('defect')}
          className="aspect-square bg-amber-600 rounded-3xl flex flex-col items-center justify-center gap-4
                     active:bg-amber-700 transition-colors shadow-lg"
        >
          <AlertTriangle size={64} className="text-white" />
          <span className="text-xl font-bold text-white text-center px-2">{t('feldModus.reportDefect')}</span>
        </button>
      </div>

      {/* Offline Status Banner */}
      {(!isOnline || pendingUploads > 0) && (
        <div className={`mx-4 mb-2 p-3 rounded-xl flex items-center justify-between
                        ${isOnline ? 'bg-yellow-900/50 border border-yellow-600' : 'bg-red-900/50 border border-red-600'}`}>
          <div className="flex items-center gap-3">
            {isOnline ? (
              <Cloud size={24} className="text-yellow-400" />
            ) : (
              <WifiOff size={24} className="text-red-400" />
            )}
            <div>
              <div className={`font-bold ${isOnline ? 'text-yellow-200' : 'text-red-200'}`}>
                {isOnline ? `${pendingUploads} ${t('feldModus.pendingUploads')}` : t('feldModus.offlineMode')}
              </div>
              <div className="text-sm text-gray-400">
                {isOnline 
                  ? t('feldModus.tapToSync')
                  : t('feldModus.uploadsSavedLocally')}
              </div>
            </div>
          </div>
          {isOnline && pendingUploads > 0 && (
            <button
              onClick={() => syncPendingUploads()}
              className="px-4 py-2 bg-yellow-600 text-white rounded-lg font-bold"
            >
              {t('feldModus.sync')}
            </button>
          )}
        </div>
      )}

      {/* Open tasks preview */}
      {openTasks.length > 0 && (
        <div className="p-4 bg-gray-800">
          <button
            onClick={() => setActiveMode('task')}
            className="w-full p-4 bg-gray-700 rounded-2xl flex items-center justify-between"
          >
            <div className="flex items-center gap-4">
              <FileText size={32} className="text-emerald-400" />
              <div className="text-left">
                <div className="text-white font-bold">{openTasks.length} {t('feldModus.openTasks')}</div>
                <div className="text-gray-400 text-sm truncate max-w-[200px]">
                  {openTasks[0]?.title}
                </div>
              </div>
            </div>
            <ChevronRight size={32} className="text-gray-500" />
          </button>
        </div>
      )}
      
      {/* Offline Indicator Floating Button */}
      <OfflineIndicator />
    </div>
  );
};

export default FeldModus;
